<template>
  <div :class="{ 'show-detail': showDetail }">
    <PageHeader title="クオリティレポート マスタ">
      <template #title-header-content>
        <div class="flex-1 flex items-center justify-end mr-2">
          <PrimaryButton text="検索" class="w-32" @click="search()" />
          <div class="ml-4 text-lg font-bold text-white whitespace-nowrap">{{ filteredSites.length }} 件</div>
          <Icon
            iconName="Refresh"
            :clickable="false"
            :class="{ 'animate-spin': loading }"
            class="ml-4 cursor-pointer text-white hover:text-gray-300"
            @click.native="search()"
          />
        </div>
      </template>

      <template #page-header-content>
        <div class="flex items-center justify-end gap-2 filters">
          <!-- 絞り込みは「検索」ボタン（または Enter）で効く -->
          <input v-model="filter.site" type="search" placeholder="物件名 検索" class="filter-input w-56 rounded border border-gray-300 px-2 text-sm" @keydown.enter="search()" />
          <input v-model="filter.client" type="search" placeholder="得意先名 検索" class="filter-input w-56 rounded border border-gray-300 px-2 text-sm" @keydown.enter="search()" />
          <input v-model="filter.address" type="search" placeholder="住所 検索" class="filter-input w-56 rounded border border-gray-300 px-2 text-sm" @keydown.enter="search()" />
          <!-- 設定の状態で絞る（初期値は全部） -->
          <select v-model="filter.status" class="filter-input rounded border-gray-300 text-sm" title="設定の状態" @change="search()">
            <option value="all">全部</option>
            <option value="none">未設定</option>
            <option value="done">設定あり</option>
          </select>
        </div>
      </template>
    </PageHeader>

    <main class="absolute top-32 -mt-1 bottom-4 w-screen">
      <div class="h-full px-4 flex gap-4">

        <!-- 左：対象物件（作業明細の「クオリティレポート」から自動。1 つ選ぶと右に撮影設定が出る） -->
        <div class="list-box flex flex-col flex-shrink-0 min-h-0">
          <div class="flex-1 min-h-0 overflow-auto rounded-md border border-gray-200 bg-white">
            <div v-if="loading" class="flex h-full items-center justify-center font-bold text-gray-400">
              Searching...
            </div>

            <div v-else-if="!filteredSites.length" class="flex h-full items-center justify-center font-bold text-gray-400">
              対象データがありません。
            </div>

            <table v-else class="w-full border-collapse text-sm">
              <thead class="sticky top-0 bg-gray-100">
                <tr class="border-b border-gray-300 text-left text-xs text-gray-600">
                  <th class="px-3 py-2">物件</th>
                  <th class="px-3 py-2">得意先・実施日</th>
                  <th class="px-3 py-2 text-center">設定</th>
                </tr>
              </thead>
              <tbody class="text-gray-700">
                <tr
                  v-for="row in pagedSites"
                  :key="row.siteCode"
                  class="border-b border-gray-200 cursor-pointer hover:bg-green-50"
                  :class="cur && row.siteCode === cur.siteCode ? 'selected-row' : ''"
                  @click="select(row)"
                >
                  <td class="px-3 py-2">
                    <div class="font-bold">{{ row.siteName }}</div>
                    <div class="text-xs text-gray-500">{{ row.siteAddress }}</div>
                  </td>
                  <td class="px-3 py-2">
                    <div>{{ row.clientName1 }}</div>
                    <div class="text-xs text-gray-500">{{ schedText(row.week, row.dow) }}<span class="ml-2">{{ row.staffName || '担当 未定' }}</span></div>
                  </td>
                  <td class="px-3 py-2 text-center whitespace-nowrap">
                    <span class="chip" :class="status(row).cls">{{ status(row).text }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- 20 件ごと（AKBS 登録済み一覧と同じ部品） -->
          <div v-if="filteredSites.length > perPage" class="my-2 w-fit mx-auto">
            <pagination id="pagenation" v-model="page" :per-page="perPage" :records="filteredSites.length" class="text-center" />
          </div>
        </div>

        <!-- 右：選んだ物件の撮影設定 -->
        <div class="detail-box flex-1 min-w-0 flex flex-col rounded-md border border-gray-200 bg-white">
          <div v-if="detailLoading" class="flex h-full items-center justify-center font-bold text-gray-400">
            Loading...
          </div>

          <div v-else-if="!draft" class="flex h-full items-center justify-center font-bold text-gray-400">
            左の一覧から物件を選んでください。
          </div>

          <template v-else>
            <div class="detail-head">
              <span class="back-btn mr-2"><SecondaryButton text="← 一覧" size="sm" @click="showDetail = false" /></span>
              <div class="min-w-0">
                <div class="text-xl font-bold text-green-900">{{ cur.siteName }}</div>
                <div class="text-xs text-gray-500">{{ cur.clientName1 }}<span v-if="cur.clientName2" class="ml-2">{{ cur.clientName2 }}</span><span class="mx-2">｜</span>{{ cur.siteAddress }}</div>
              </div>
              <!-- 保存ボタンは右端に -->
              <span class="mx-auto"></span>
              <PrimaryButton text="保存" size="lg" class="w-40 justify-center" :disabled="saving || noTotal" @click="save()" />
            </div>

            <div class="flex-1 min-h-0 overflow-auto px-4 py-3 flex flex-col gap-3">
              <!-- 実施中の報告書があるときは箇所の名前・並び・追加・削除は変えられない（報告書の箇所は開いたときに写してあり、終了時に報告書の内容でマスタが上書きされるため）。
                   物件の設定（枚数・実施日・祝日のとき・担当者）と★は変えられる（★は実施中の報告書にも写す） -->
              <div v-if="locked" class="alert bg-red-50 text-red-700 font-bold">
                <span class="whitespace-pre-line">{{ lockedText }}</span>
              </div>

              <!-- この画面でしか変えられないもの・報告書画面でも変えられるものの説明（お客様の要望） -->
              <ul class="notes text-sm font-bold text-red-600">
                <li>①「合計写真枚数」はマスタでのみ設定可（必須）</li>
                <li>②「お客様ご指定」はマスタでのみ設定可</li>
                <li>③項目の「箇所」「表示順」は報告書画面で変更可、次回に引継がれる</li>
              </ul>

              <!-- 合計写真枚数（最低ラインの枚数。現地ではこれ以上撮ってよい。変えられるのはこの画面だけ）・実施日 -->
              <div class="flex gap-3 flex-wrap">
                <div class="tile">
                  <div class="text-xs text-gray-500">合計写真枚数 <span class="req-mark">*</span></div>
                  <div class="flex items-baseline gap-1">
                    <!-- 未入力・1 未満・小数は赤（初期値は入れない）。小数点・マイナスはキーで止め、欄を出るときに 1 以上の整数に直す -->
                    <input
                      v-model.number="draft.totalPhoto"
                      type="number"
                      min="1"
                      step="1"
                      class="total-input rounded text-2xl font-bold"
                      :class="noTotal ? 'border-red-500 bg-red-50' : 'border-gray-300'"
                      @wheel="$event.target.blur()"
                      @keydown="blockNonDigit"
                      @blur="fixTotal"
                    />
                    <span class="text-xs text-gray-500">枚</span>
                  </div>
                  <div class="text-xs text-gray-600">問題個所が多い時は設定枚数以上添付可能</div>
                </div>
                <div class="tile">
                  <div class="text-xs text-gray-500">実施日（目安）</div>
                  <div class="flex items-center gap-2 flex-wrap text-base">
                    <select v-model="draft.week" class="rounded border-gray-300 font-bold py-1">
                      <option v-for="w in weeks" :key="'w' + w.v" :value="w.v">{{ w.n }}</option>
                    </select>
                    <select v-model="draft.dow" class="rounded border-gray-300 font-bold py-1">
                      <option v-for="d in dows" :key="'d' + d.v" :value="d.v">{{ d.n }}</option>
                    </select>
                    <!-- 祝日のとき（同じ枠の中で横並び。見出しは選択肢の上）。同じ種類の切り替えボタンは幅をそろえる。月はまたがず、月内に無ければ反対側へ -->
                    <div class="ml-3 flex flex-col">
                      <span class="text-xs text-gray-500 -mt-4">祝日のとき</span>
                      <span class="flex items-center gap-2">
                        <button type="button" class="seg-btn" :class="Number(draft.holidayShift) !== 2 ? 'is-on' : ''" @click="draft.holidayShift = 1">翌日以降</button>
                        <button type="button" class="seg-btn" :class="Number(draft.holidayShift) === 2 ? 'is-on' : ''" @click="draft.holidayShift = 2">前日以前</button>
                      </span>
                    </div>
                    <span class="text-xs text-gray-500 ml-2">{{ nextDateText }}</span>
                  </div>
                </div>
                <div class="tile">
                  <div class="text-xs text-gray-500">担当者</div>
                  <!-- 候補はユーザー設定で「クオリティレポート作業員に登録」した人（patrolStaff/get type=5）。共通の検索付き単一選択（× で未定に戻せる） -->
                  <WmsSearchSelect
                    name="staff"
                    :value="draft.staff"
                    :options="staffList"
                    track-by="userId"
                    label="name"
                    placeholder=""
                    class="w-56"
                    @input="draft.staff = $event"
                  />
                </div>
              </div>

              <!-- 撮影する箇所（1 箇所 1 枚。合計写真枚数は最低ラインなので、箇所の数が枚数より多くてもよい） -->
              <div class="rounded-md border border-gray-200 overflow-auto">
                <table class="item-table w-full text-sm">
                  <thead class="bg-gray-100 text-xs text-gray-600">
                    <tr>
                      <th class="w-8"></th>
                      <th class="w-10 px-2 py-2 text-left">No.</th>
                      <th class="px-2 py-2 text-left">箇所（どこを撮るか）</th>
                      <th class="w-36 px-2 py-2"></th>
                      <th class="w-10"></th>
                    </tr>
                  </thead>
                  <draggable :list="draft.items" tag="tbody" handle=".row-drag-handle" direction="vertical" :disabled="locked">
                    <tr
                      v-for="(it, i) in draft.items"
                      :key="it.key"
                      class="border-t border-gray-200"
                      :class="{ 'client-row': it.clientSpecified }"
                    >
                      <td class="text-center">
                        <div class="row-drag-handle inline-flex p-1 text-gray-400 cursor-grab" title="つかんで並べ替え">
                          <MenuIcon class="w-5 h-5" />
                        </div>
                      </td>
                      <td class="px-2 py-1 text-gray-500">{{ i + 1 }}</td>
                      <td class="px-2 py-1">
                        <input v-model="it.item" type="text" class="item-name w-full rounded border-gray-300 text-sm py-1" :disabled="locked" />
                      </td>
                      <td class="px-2 py-1">
                        <!-- お客様ご指定（★）の行は現地で箇所名を変えられない・消せない。★の無い行は現地で撮りながら箇所名を変えてよい。実施中でも付け外しできる -->
                        <button
                          type="button"
                          class="star-btn"
                          :class="it.clientSpecified ? 'is-on' : ''"
                          :aria-pressed="it.clientSpecified"
                          @click="it.clientSpecified = !it.clientSpecified"
                        >
                          <StarIcon v-if="it.clientSpecified" class="w-4 h-4" />
                          <StarOutlineIcon v-else class="w-4 h-4" />
                          お客様ご指定
                        </button>
                      </td>
                      <td class="text-center">
                        <TrashIcon v-if="!locked" class="inline w-5 h-5 text-gray-400 hover:text-red-600 cursor-pointer" title="この行を削除" @click="draft.items.splice(i, 1)" />
                      </td>
                    </tr>
                  </draggable>
                </table>
              </div>

              <div v-if="!locked">
                <SecondaryButton text="＋ 行を追加" size="sm" @click="addRow()" />
              </div>
            </div>
          </template>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import Icon from '@components/Icon.vue'
import PageHeader from '@components/PageHeader.vue'
import PrimaryButton from '@components/PrimaryButton.vue'
import SecondaryButton from '@components/SecondaryButton.vue'
import draggable from 'vuedraggable'
import Pagination from 'vue-pagination-2'
import WmsSearchSelect from '@wmscomponents/WmsSearchSelect.vue'
import { MenuIcon, TrashIcon, StarIcon } from '@vue-hero-icons/solid'
import { StarIcon as StarOutlineIcon } from '@vue-hero-icons/outline'
import * as backend from '@libs/backend'
import * as dialogs from '@libs/dialogs'
import * as logManager from '@managers/logManager'
import moment from 'moment'
import PageModel from '../../main/pages/PageModel.vue'

// 未設定の物件を開いたときの初期値
const DEFAULT_ITEMS = ['エントランス', '郵便受け・掲示板', 'ゴミ置場', '駐輪場', '共用廊下', '共用階段', '外周・植栽']
const DOW_NAME = ['日', '月', '火', '水', '木', '金', '土']

let rowKey = 0

export default {
  extends: PageModel,
  components: {
    Icon,
    PageHeader,
    PrimaryButton,
    SecondaryButton,
    draggable,
    Pagination,
    WmsSearchSelect,
    MenuIcon,
    TrashIcon,
    StarIcon,
    StarOutlineIcon
  },

  data() {
    return {
      loading: false,
      detailLoading: false,
      saving: false,
      // 対象物件（作業明細から）
      sites: [],
      // 担当者の候補（クオリティレポート作業員）
      staffList: [],
      // 絞り込み（入力中）
      filter: { site: '', client: '', address: '', status: 'all' },
      // 絞り込み（検索ボタンで効かせた分）
      applied: { site: '', client: '', address: '', status: 'all' },
      // ページ（20 件ごと）
      page: 1,
      perPage: 20,
      // 選んでいる物件
      cur: null,
      // 編集中の設定（totalPhoto・week・dow・items）
      draft: null,
      // 実施中の報告書（あればこの物件のマスタは変えられない）
      doing: null,
      // 読み込み直後・保存直後の内容（変更があるかの比較用）
      original: '',
      // スマホで詳細を表示中
      showDetail: false,
      weeks: [
        { v: '', n: '未定' }, { v: 1, n: '第1' }, { v: 2, n: '第2' }, { v: 3, n: '第3' }, { v: 4, n: '第4' }
      ],
      // 実施日の曜日は平日だけ（土日は無し）
      dows: [
        { v: '', n: '―' }, { v: 1, n: '月曜日' }, { v: 2, n: '火曜日' }, { v: 3, n: '水曜日' }, { v: 4, n: '木曜日' }, { v: 5, n: '金曜日' }
      ]
    }
  },

  computed: {
    /**
     * 絞り込み後の一覧
     */
    filteredSites() {
      const f = this.applied
      return this.sites.filter((s) => {
        // 設定の状態（未設定＝箇所の行が 1 つも無い）
        const none = !Number(s.itemCount)
        if (f.status === 'none' && !none) {
          return false
        }
        if (f.status === 'done' && none) {
          return false
        }
        return (s.siteName || '').includes(f.site)
          && ((s.clientName1 || '') + (s.clientName2 || '')).includes(f.client)
          && (s.siteAddress || '').includes(f.address)
      })
    },

    /**
     * 表示中のページの分
     */
    pagedSites() {
      const start = (this.page - 1) * this.perPage
      return this.filteredSites.slice(start, start + this.perPage)
    },

    /**
     * 合計写真枚数が未入力か 1 以上の整数でないか（必須。保存できない）
     */
    noTotal() {
      if (!this.draft) {
        return false
      }
      const v = this.draft.totalPhoto
      const n = Number(v)
      return v === '' || v === null || !Number.isInteger(n) || n < 1
    },

    /**
     * 実施中の報告書があって、箇所の名前・並び・追加・削除を変えられないか（物件の設定と★は変えられる）
     */
    locked() {
      return !!this.doing
    },

    /**
     * 変えられない理由の文
     */
    lockedText() {
      if (!this.doing) {
        return ''
      }
      const month = this.doing.targetMonth ? moment(this.doing.targetMonth).format('YYYY年M月') : ''
      // 「。」で改行（pre-line で表示）
      return [
        `${month}分の報告書が実施中のため（担当 ${this.doing.staffName || '未定'}）、箇所の名前・並び・追加・削除はこの画面では変更できません。`,
        '報告書画面で変更できます。',
        '終了するとその内容がこの設定に反映されます。',
        'お客様ご指定・合計写真枚数・実施日・担当者は保存できます。'
      ].join('\n')
    },

    /**
     * 変更があるか
     */
    isDirty() {
      return !!this.draft && this.snapshot(this.draft) !== this.original
    },

    /**
     * 実施日（目安）の次の日付
     */
    nextDateText() {
      if (!this.draft || this.draft.week === '' || this.draft.dow === '') {
        return ''
      }
      const d = this.nextDate(this.draft.week, this.draft.dow)
      return d ? `次回 ${d.format('M/D')}（${DOW_NAME[d.day()]}）` : ''
    }
  },

  async created() {
    await Promise.all([this.getSites(), this.getStaffList()])
    await this.openFromQuery()
  },

  methods: {
    /**
     * URL に物件コードが付いていたら（予定・結果の「マスタ」リンク）、その物件を選んだ状態で開く
     */
    async openFromQuery() {
      const code = this.$route.query.siteCode
      if (!code) {
        return
      }
      const idx = this.filteredSites.findIndex((s) => s.siteCode === code)
      if (idx < 0) {
        await dialogs.showErrorDialog('物件', 'この物件は対象物件の一覧にありません。')
        return
      }
      // その行のページを開いてから選ぶ
      this.page = Math.floor(idx / this.perPage) + 1
      await this.select(this.filteredSites[idx])
      await this.$nextTick()
      const el = this.$el.querySelector('.selected-row')
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ block: 'center' })
      }
    },

    /**
     * 担当者の候補（クオリティレポート作業員に登録した人）
     * API: patrolStaff/get
     */
    async getStaffList() {
      try {
        const res = await backend.searchData('patrolStaff/get', { type: 5 })
        this.staffList = (res.data && res.data.data) || []
      } catch (e) {
        console.log(e)
        this.staffList = []
      }
    },

    /**
     * 検索ボタン：絞り込みを効かせて 1 ページ目へ（一覧も取り直す）
     */
    async search() {
      this.applied = { ...this.filter }
      this.page = 1
      await this.getSites()
    },

    /**
     * 対象物件の一覧を取得
     * API: qualityReport/sites
     */
    async getSites() {
      this.loading = true
      try {
        const res = await backend.searchData('qualityReport/sites', {})
        this.sites = (res.data && res.data.data) || []
      } catch (e) {
        console.log(e)
        await dialogs.showErrorDialog('エラー', '対象物件の取得に失敗しました。')
      }
      this.loading = false
    },

    /**
     * 一覧の状態チップ
     */
    status(row) {
      if (!Number(row.itemCount)) {
        return { cls: 'chip-none', text: '未設定' }
      }
      if (row.totalPhoto === null || row.totalPhoto === undefined || row.totalPhoto === '') {
        return { cls: 'chip-warn', text: '合計枚数 未入力' }
      }
      return { cls: 'chip-ok', text: `${row.totalPhoto} 枚 設定済` }
    },

    /**
     * 実施日の文字
     */
    schedText(week, dow) {
      if (week === null || week === undefined || week === '' || dow === null || dow === undefined || dow === '') {
        return '実施日 未定'
      }
      return `毎月 第${week} ${DOW_NAME[Number(dow)]}曜日`
    },

    /**
     * 今日以降で最初の「第 n ●曜日」
     */
    nextDate(week, dow) {
      const today = moment().startOf('day')
      for (let k = 0; k < 3; k++) {
        const month = moment().startOf('month').add(k, 'months')
        const d = month.clone()
        while (d.day() !== Number(dow)) {
          d.add(1, 'days')
        }
        d.add((Number(week) - 1) * 7, 'days')
        if (!d.isBefore(today)) {
          return d
        }
      }
      return null
    },

    /**
     * 比較用の文字列
     */
    snapshot(d) {
      return JSON.stringify({
        totalPhoto: d.totalPhoto,
        week: d.week,
        dow: d.dow,
        holidayShift: Number(d.holidayShift) === 2 ? 2 : 1,
        staffId: d.staff ? Number(d.staff.userId) : '',
        items: d.items.map((i) => [i.item, !!i.clientSpecified])
      })
    },

    /**
     * 担当者 ID → 候補のオブジェクト（無ければ null。候補から外れた人は名前だけの仮の候補）
     */
    findStaff(staffId, staffName) {
      if (staffId === null || staffId === undefined || staffId === '') {
        return null
      }
      const s = this.staffList.find((x) => Number(x.userId) === Number(staffId))
      if (s) {
        return s
      }
      return { userId: Number(staffId), name: (staffName || '不明') + '（作業員の登録なし）' }
    },

    /**
     * 新しい行
     */
    newItem(item, clientSpecified, id) {
      // id はマスタの行の id（実施中の保存で★を id で更新する。新しく足した行は無し）
      return { key: ++rowKey, id: id || null, item: item || '', clientSpecified: !!clientSpecified }
    },

    /**
     * 物件を選ぶ → 右に撮影設定を出す
     * API: qualityReport/setting
     */
    async select(row) {
      // 読み込み中の連打は無視
      if (this.detailLoading) {
        return
      }
      if (this.cur && row.siteCode === this.cur.siteCode && this.draft) {
        // 同じ物件を押しても読み直さない（編集中の内容を消さない）
        this.showDetail = true
        return
      }
      // 未保存のまま別の物件を押したときは確認（はい＝変更を捨てて移動）
      if (this.cur && this.isDirty) {
        const r = await dialogs.showConfirmDialog('確認', '保存していない変更があります。移動してよろしいですか？', 'はい', 'キャンセル')
        if (r !== 'YES') {
          return
        }
      }
      this.cur = row
      this.showDetail = true
      this.detailLoading = true
      let setting = { site: null, items: [], doing: null }
      try {
        const res = await backend.searchData('qualityReport/setting', { siteCode: row.siteCode })
        if (res.data && res.data.data) {
          setting = res.data.data
        }
      } catch (e) {
        console.log(e)
        this.detailLoading = false
        await dialogs.showErrorDialog('エラー', '撮影設定の取得に失敗しました。')
        return
      }

      // 実施中の報告書があれば見るだけ
      this.doing = setting.doing || null
      const site = setting.site || {}
      const draft = {
        totalPhoto: site.totalPhoto === null || site.totalPhoto === undefined ? '' : site.totalPhoto,
        week: site.week === null || site.week === undefined ? '' : site.week,
        dow: site.dow === null || site.dow === undefined ? '' : site.dow,
        // 祝日の振替（初期値は翌日以降）
        holidayShift: Number(site.holidayShift) === 2 ? 2 : 1,
        // 担当者は multiselect 用に候補のオブジェクトで持つ（候補から外れた人は名前だけの仮の候補にして残す）
        staff: this.findStaff(site.staffId, row.staffName),
        items: (setting.items || []).map((i) => this.newItem(i.item, i.clientSpecified, i.id))
      }
      // 未設定なら箇所の初期値を入れて知らせる（保存はまだ。合計写真枚数は入れない。実施中なら入れない）
      const filled = !draft.items.length && !this.doing
      if (filled) {
        draft.items = DEFAULT_ITEMS.map((n) => this.newItem(n, false))
      }
      this.draft = draft
      // 初期値を入れただけの状態も「元」にする（見ただけで別の物件へ移るときに確認を出さない）
      this.original = this.snapshot(draft)
      this.detailLoading = false

      if (filled) {
        await dialogs.showInfoDialog('初期値を入れました', '未設定のため、初期値を入れました。\r\n編集完了後は保存ボタンを押してください。')
      }
    },

    /**
     * 合計写真枚数：小数点・マイナス・指数のキーを通さない（type=number はそのままだと入る）
     */
    blockNonDigit(e) {
      if (['.', '-', '+', 'e', 'E', ','].includes(e.key)) {
        e.preventDefault()
      }
    },

    /**
     * 合計写真枚数：欄を出るときに 1 以上の整数に直す（貼り付けなどで入った小数は切り捨て、1 未満は空にして必須のエラーに）
     */
    fixTotal() {
      const n = parseInt(this.draft.totalPhoto, 10)
      this.draft.totalPhoto = Number.isInteger(n) && n >= 1 ? n : ''
    },

    /**
     * 行を追加（箇所は空。入力欄にフォーカス）
     */
    addRow() {
      this.draft.items.push(this.newItem('', false))
      this.$nextTick(() => {
        const inputs = this.$el.querySelectorAll('.item-name')
        if (inputs.length) {
          inputs[inputs.length - 1].focus()
        }
      })
    },

    /**
     * 保存（途中でも保存できる。合計写真枚数が未入力のときは保存しない）
     * 実施中の報告書があるとき（locked）は doingId を送り、バックは物件の設定と★だけ保存する（箇所は入れ直さない）
     * API: qualityReport/saveSetting
     */
    async save() {
      if (!this.draft || this.saving) {
        return false
      }
      if (this.noTotal) {
        await dialogs.showErrorDialog('保存できません', '合計写真枚数を 1 以上の整数で入力してください。')
        return false
      }
      this.saving = true
      const d = this.draft
      const param = {
        siteCode: this.cur.siteCode,
        totalPhoto: Number(d.totalPhoto),
        week: d.week === '' ? null : Number(d.week),
        dow: d.dow === '' ? null : Number(d.dow),
        holidayShift: Number(d.holidayShift) === 2 ? 2 : 1,
        staffId: d.staff ? Number(d.staff.userId) : null,
        // 箇所が未入力の行は保存しない
        items: d.items.filter((i) => i.item.trim()).map((i) => ({ id: i.id, item: i.item.trim(), clientSpecified: !!i.clientSpecified })),
        doingId: this.doing ? this.doing.id : null,
        userId: this.$store.getters.user.id
      }
      try {
        const res = await backend.postData('qualityReport/saveSetting', param)
        if (res.data !== 'complete') {
          throw new Error(res.data)
        }
      } catch (e) {
        console.log(e)
        this.saving = false
        await dialogs.showErrorDialog('保存失敗', 'エラー発生\r\n保存に失敗しました。')
        return false
      }
      this.original = this.snapshot(d)
      // 一覧の状態チップを直す
      const row = this.sites.find((s) => s.siteCode === this.cur.siteCode)
      if (row) {
        row.totalPhoto = param.totalPhoto
        row.week = param.week
        row.dow = param.dow
        row.holidayShift = param.holidayShift
        row.staffId = param.staffId
        row.staffName = d.staff ? d.staff.name : null
        row.itemCount = param.items.length
      }
      this.saving = false

      logManager.recording(this, logManager.Loglevel.INFO, 'クオリティレポート', '物件設定', `物件名：${this.cur.siteName}（物件コード：${this.cur.siteCode}）の撮影設定を保存しました。`)
      await dialogs.showSuccessDialog('保存完了', `物件名：${this.cur.siteName} の撮影設定を保存しました。`)
      return true
    }
  }
}
</script>

<style scoped>
/* 左の一覧の枠（AKBS 登録済み一覧と同じ幅） */
.list-box {
  width: 460px;
}

/* 選んでいる行（メニューのカードと同じ緑系で揃える） */
.selected-row {
  background-color: #d1fae5;
  font-weight: bold;
}

.filter-input {
  height: 38px;
  box-sizing: border-box;
}

.chip {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  padding: 1px 9px;
  font-size: 12px;
  font-weight: bold;
}

.chip-ok {
  background: #ecfdf5;
  color: #047857;
}

.chip-warn {
  background: #fffbeb;
  color: #b45309;
}

.chip-none {
  background: #f3f4f6;
  color: #6b7280;
}

.detail-head {
  padding: 10px 16px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.tile {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.total-input {
  width: 84px;
  padding: 0 8px;
  text-align: center;
}

/* 必須の印（大きく目立たせる） */
.req-mark {
  color: #dc2626;
  font-size: 22px;
  font-weight: 900;
  line-height: 1;
  vertical-align: middle;
}

/* 赤字の説明（3 行。字下げなし） */
.notes {
  list-style: none;
  padding: 0;
  margin: 0;
  line-height: 1.6;
}

.alert {
  border-radius: 6px;
  padding: 7px 12px;
  font-size: 13px;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.item-table td {
  vertical-align: middle;
}

/* お客様ご指定の行 */
.client-row td {
  background: #ecfdf5;
}

.client-row .item-name {
  background: #fff;
  border-color: #059669;
}

/* 祝日の振替の切り替え（同じ幅） */
.seg-btn {
  width: 96px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #6b7280;
  padding: 5px 0;
  font-size: 14px;
}

/* 状態のクラスは is-on（共通 CSS の .on は緑＋白文字が !important で付くので使わない） */
.seg-btn.is-on {
  background: #d1fae5;
  color: #047857;
  border-color: #059669;
  font-weight: bold;
}

.star-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  width: 128px;
  justify-content: center;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #6b7280;
  padding: 4px 8px;
  font-size: 13px;
  white-space: nowrap;
}

.star-btn.is-on {
  background: #d1fae5;
  color: #047857;
  border-color: #059669;
  font-weight: bold;
}

.back-btn {
  display: none;
}

/* ページ送り（AKBS 登録済み一覧と同じ見た目） */
#pagenation >>> ul {
  display: flex !important;
  color: rgb(6, 125, 194);
  font-weight: bold !important;
  cursor: pointer;
}

#pagenation >>> ul > li {
  padding: 0 10px;
}

#pagenation >>> ul > .active {
  background: rgb(6, 125, 194);
  color: white;
}

#pagenation >>> p {
  color: gray;
  font-size: small;
}

/* スマホ：一覧 → 詳細の切り替え */
@media screen and (max-width: 820px) {
  .list-box {
    width: 100%;
  }

  .show-detail .list-box {
    display: none;
  }

  div:not(.show-detail) > main .detail-box {
    display: none;
  }

  .back-btn {
    display: inline-flex;
  }

  .filters .filter-input {
    flex: 1 1 100px;
    min-width: 0;
    width: auto;
  }
}
</style>
